import type { ReactNode } from "react";
import { type HTMLAttributes, useCallback } from "react";

import { MdDelete, MdSend } from "react-icons/md";
import * as Transform from "../../utility/transforms";
import * as Validator from "../../utility/validators";
import { useForm } from "../form";
import { useEditor } from "../generic-editor";

import { InputField, type InputProps } from "./input";
import { SelectField } from "./select";
import { TextAreaField } from "./textarea";


export function EmailField(props: InputProps) {
  return <InputField {...props} type="email" inboundTransform={Transform.lowerCase} validator={Validator.email} placeholder="Your Email Address "/>;
}

export function PasswordField(props: InputProps) {
  return <InputField {...props} type="password" validator={Validator.password} placeholder="Password"/>;
}

export function PasswordConfirmField({ siblingName, ...props }: InputProps & { siblingName: string }) {
  const form = useForm();
  return <InputField {...props} type="password" validator={Validator.passwordConfirm(siblingName, form)} placeholder="Confirm Password"/>;
}

export function FullNameField(props: InputProps) {
  return <InputField {...props} type="name" validator={Validator.name} placeholder="Your Name"/>;
}

export function ProductNameField(props: InputProps) {
  return <InputField {...props} type="name" validator={Validator.productName} placeholder="Product Name"/>;
}

export function CompanyNameField(props: InputProps) {
  return <InputField {...props} type="company" validator={Validator.company} placeholder="Company Name"/>;
}

export function WebsiteURIField(props: InputProps) {
  return <InputField {...props} type="uri" validator={Validator.uri} placeholder="Website URI"/>;
}

export function TaxIDField(props: InputProps) {
  return <InputField {...props} type="taxId" validator={Validator.taxId} placeholder="Tax ID"/>;
}

export function PhoneField(props: InputProps) {
  return <InputField {...props} type="phone" placeholder="Phone Number"/>;
}

export function DomainField(props: InputProps) {
  return <InputField {...props} type="domain" validator={Validator.domain} inboundTransform={Transform.lowerCase} placeholder="myDomain.com"/>;
}

export function CategoryNameField(props: InputProps) {
  return <InputField {...props} type="categoryName" validator={Validator.categoryName} placeholder="Product Category"/>;
}

export function GenericNameField(props: InputProps) {
  return <InputField {...props} type="categoryName" validator={Validator.categoryName} placeholder="Name"/>;
}

export function ObjectCodeField(props: InputProps) {
  return <InputField {...props} type="code" placeholder="Code"/>;
}

export function TagsField(props: InputProps) {
  return (
    <InputField {...props} type="tags" placeholder="Tags" inboundTransform={Transform.array2delimited(",")} outboundTransform={Transform.delimited2array(",")}/>
  );
}

export function DateField(props: InputProps) {
  return <InputField {...props} type="date" placeholder="Date"/>; // InboundTransform={ Transform.date2ymd } outboundTransform={ Transform.ymd2date }/>;
}

export function HiddenField(props: InputProps) {
  return <InputField {...props} type="hidden" style={{ display: "none" }}/>;
}

export function UUIDField(props: InputProps) {
  return <InputField {...props} readOnly type="string"/>;
}

export function StringField(props: InputProps) {
  return <InputField {...props} type="string"/>;
}

export function NumberField(props: InputProps) {
  return <InputField {...props} type="number" placeholder="0.00" inboundTransform={String} outboundTransform={Number}/>;
}

export function IntegerField(props: InputProps) {
  return <InputField {...props} type="number" placeholder="0" inboundTransform={String} outboundTransform={Number}/>;
}

export function TimeIntervalField(props: InputProps) {
  return <InputField {...props} type="text" placeholder="00:00:00"/>;
}

export function TimeOfDayField(props: InputProps) {
  return <InputField {...props} type="time" placeholder=""/>;
}

export function TimestampField(props: InputProps) {
  return <InputField {...props} type="datetime" placeholder=""/>;
}

export function TimezoneField(props: InputProps) {
  return <InputField {...props} type="timezone" placeholder="Timezone Name"/>;
}

export function CurrencyField(props: InputProps) {
  return <InputField {...props} prefix="$" type="string" placeholder="0.00"/>;
}

export function SubmitField({ className = "", ...props }: HTMLAttributes<HTMLButtonElement>) {
  const form = useForm();
  const enabled = form?.valid && !form.disabled && form.dirty;

  return (
    <button
      type="submit"
      className={`w-auto py-1.5 px-3 rounded text-white text-base font-semibold transition-colors flex items-center gap-2 ${
        enabled ? "bg-[#ee6e73] hover:bg-[#e05560] cursor-pointer" : "bg-[#ccc] text-[#888] cursor-not-allowed"
      } ${className}`}
      {...props}
    >
      <MdSend/>
      {props.children}
    </button>
  );
}

export function DeleteField({ className = "", ...props }: HTMLAttributes<HTMLButtonElement>) {
  const form = useForm();
  const editor = useEditor();

  const handleClick = useCallback(() => editor?.deleteRecord(), [editor]);
  const enabled = !form?.disabled;

  return (
    <button
      type="button"
      className={`w-auto py-1.5 px-3 rounded text-white text-base font-semibold transition-colors flex items-center gap-2 ${
        enabled ? "bg-[#ee6e73] hover:bg-[#e05560] cursor-pointer" : "bg-[#ccc] text-[#888] cursor-not-allowed"
      } ${className}`}
      onClick={handleClick}
      {...props}
    >
      <MdDelete/>
      {props.children}
    </button>
  );
}

export function JSONEditorField(props: Parameters<typeof TextAreaField>["0"]) {
  return <TextAreaField {...props} inboundTransform={Transform.jsonToString} outboundTransform={Transform.stringToJSON} validator={Validator.json}/>;
}

export { MarkdownField as Markdown } from "./markdown";

export function BooleanSelectField({ children, ...props }: Parameters<typeof SelectField>["0"] & { children: ReactNode }) {
  return (
    <SelectField {...props} inboundTransform={Transform.bool2string} outboundTransform={Transform.string2bool}>
      {children}
    </SelectField>
  );
}

export { AutoCompleteField } from "./autocomplete";
export { FieldSet } from "./fieldset";
export { InputField } from "./input";
export { OptionField, SelectField } from "./select";
export { TextAreaField } from "./textarea";
export { ValidityIndicator } from "./validityIndicator";

export { TurnstileField } from "./turnstile";
